import { cn } from '../../lib/cn.ts';

export type SparklineTone = 'primary' | 'accent' | 'success' | 'danger';

export interface SparklineProps {
  /** The series, oldest first. Fewer than two points draws nothing. */
  values: number[];
  width?: number;
  height?: number;
  /** Defaults to `primary` — the trend beside a figure, not a status. */
  tone?: SparklineTone;
  /** Marks where the series ends. */
  showLastPoint?: boolean;
  /** What the line traces, e.g. "Sessions over the last 30 days". */
  ariaLabel: string;
  className?: string;
}

const STROKES: Record<SparklineTone, string> = {
  primary: 'stroke-ink',
  accent: 'stroke-accent',
  success: 'stroke-success',
  danger: 'stroke-danger',
};

const FILLS: Record<SparklineTone, string> = {
  primary: 'fill-ink',
  accent: 'fill-accent',
  success: 'fill-success',
  danger: 'fill-danger',
};

/** Vertical breathing room so the peak and the trough are not clipped flat. */
const PAD_Y = 2;

interface Point {
  x: number;
  y: number;
}

function points(values: number[], width: number, height: number): Point[] {
  const high = Math.max(...values);
  const low = Math.min(...values);
  const span = high - low;
  const usable = height - PAD_Y * 2;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;

  return values.map((value, index) => ({
    x: index * stepX,
    /*
     * A flat series sits in the middle rather than on the floor: pinned to the
     * bottom it would read as zero, which is a different fact from "unchanged".
     */
    y: span === 0 ? height / 2 : height - PAD_Y - ((value - low) / span) * usable,
  }));
}

/**
 * The shape of a number's recent history, small enough to sit inside a row.
 *
 * No axes, no scale, no ticks: it answers "up, down or flat" beside the figure
 * that says how much, and anything more turns a table row into a chart.
 */
export function Sparkline({
  values,
  width = 96,
  height = 24,
  tone = 'primary',
  showLastPoint = false,
  ariaLabel,
  className,
}: SparklineProps) {
  const plotted = values.length > 1 ? points(values, width, height) : [];
  const path = plotted
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`,
    )
    .join(' ');
  const last = plotted[plotted.length - 1];

  return (
    <svg
      data-slot='sparkline'
      role='img'
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
    >
      {path !== '' && (
        <path
          data-slot='sparkline-path'
          d={path}
          fill='none'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
          className={STROKES[tone]}
        />
      )}
      {showLastPoint && last !== undefined && (
        <circle
          data-slot='sparkline-point'
          cx={last.x}
          cy={last.y}
          r={2}
          className={FILLS[tone]}
        />
      )}
    </svg>
  );
}
